import { createApp, watch } from 'vue'
import { createI18n } from 'vue-i18n'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import Toast, { POSITION } from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import VueGtag from 'vue-gtag'
import { getAcceptedCookies } from '@/stores/cookies'
import { registerSW } from 'virtual:pwa-register'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import App from './App.vue'
import router from './router'
import messages from '@intlify/unplugin-vue-i18n/messages'

// Firebase imports
import { initializeApp } from 'firebase/app'
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'

// Register service worker
const updateSW = registerSW({
  onNeedRefresh() {
    // The PWAUpdatePrompt component will handle this in App.vue
    window.dispatchEvent(new CustomEvent('pwa-update-available'))
  },
  immediate: true,
  onRegisterError(error) {
    console.error('SW registration error', error)
  }
})

// Export updateSW so it can be used in components
export { updateSW }

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyDk9f4A2zbHOL5IuWxS3CdI1a_nnOSdOD8',
  authDomain: 'allez--allez.firebaseapp.com',
  projectId: 'allez--allez',
  storageBucket: 'allez--allez.appspot.com',
  messagingSenderId: '838907708699',
  appId: '1:838907708699:web:6b680b5d336bcca3e9bfbe',
  measurementId: 'G-G369T2BFP0'
}

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig)

// Enable App Check debug token for local development if provided.
// In dev, setting this to true will log a token in the console on first load.
// Copy that token into Firebase Console > App Check > Manage debug tokens to trust this device.
if (import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true
}

// Set up i18n
const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: 'fr',
  fallbackLocale: 'en',
  availableLocales: ['fr', 'en'],
  messages: messages
})

// Keep <html lang> in sync with the active locale so assistive tech announces pronunciation correctly.
const i18nLocale = i18n.global.locale as { value: string }
document.documentElement.lang = i18nLocale.value
watch(
  () => i18nLocale.value,
  (next) => {
    document.documentElement.lang = next
  }
)

// Create Vue app
const app = createApp(App)

// global variables
const signedInUser = null
app.config.globalProperties.$signedInUser = signedInUser

const acceptedCookies = getAcceptedCookies()
const gtagEnabled = acceptedCookies ? acceptedCookies.analythics : false

// Set up Pinia
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

// Set up App Check (reCAPTCHA v3) — must run immediately after initializeApp
// Prefer using a dedicated key generated in Firebase Console > App Check for your Web app.
const appCheckSiteKey =
  import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY || import.meta.env.VITE_RECAPTCHA_SITE_KEY
if (appCheckSiteKey) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey as string),
    isTokenAutoRefreshEnabled: true
  })
} else {
  console.warn(
    '[AppCheck] No site key found (VITE_APPCHECK_RECAPTCHA_SITE_KEY). App Check not initialized.'
  )
}

// Initialize Firebase services AFTER App Check so tokens are attached to outgoing requests
const db = getFirestore(firebaseApp)
const storage = getStorage(firebaseApp)
const functions = getFunctions(firebaseApp)

// Local development: route Firestore, Auth, and callable functions to the emulator
// suite instead of production. Opt-in via `VITE_USE_EMULATORS=true` so this never
// affects normal `npm run dev` (which talks to production).
//
// All three must be emulated together: the blacklist callable validates challenge
// existence against the SAME Firestore that the UI reads its challenge catalogue from.
// Emulating only functions splits reads (prod) from write-validation (emulator) — see the
// `unknown-challenge` failure that motivated this. Ports come from `emulators` in firebase.json.
//
// Start the suite with: `firebase emulators:start --only functions,firestore,auth`
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectAuthEmulator(getAuth(firebaseApp), 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
  console.info('[Emulators] Connected Firestore (8080), Auth (9099), Functions (5001)')
}

export { db, storage, functions }

// Set up reCAPTCHA v3 for Vue components (separate key recommended for user actions)
const recaptchaV3SiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
if (recaptchaV3SiteKey) {
  app.use(VueReCaptcha, {
    siteKey: recaptchaV3SiteKey,
    loaderOptions: { autoHideBadge: true }
  })
} else {
  console.warn('[reCAPTCHA] VITE_RECAPTCHA_SITE_KEY is not set; vue-recaptcha-v3 will be disabled.')
}

// Set up additional plugins
app.use(
  VueGtag,
  {
    appName: 'Allez Allez',
    enabled: gtagEnabled,
    pageTrackerScreenviewEnabled: true,
    config: { id: 'G-G369T2BFP0' }
  },
  router
)
app.use(router)
app.use(Toast, { position: POSITION.TOP_CENTER })
app.use(i18n)

// Mount the app
app.mount('#app')
export default app
